"use client";
import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import AOS from 'aos';
import 'aos/dist/aos.css';

import OrderModal from '../../components/shared/OrderModal';
import CouponComponent from '../../components/pricing/Coupon';
import OrderSuccessModal from '../../components/pricing/OrderSuccessModal';

export default function Pricing({ isHomePage = false }) {
  const rawPath = usePathname();
  const pathname = rawPath !== '/' ? rawPath.replace(/\/$/, '') : rawPath;
  const isStandalonePricingPage = pathname === '/pricing';

  const [discount, setDiscount] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [orderSubmitted, setOrderSubmitted] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true, easing: 'ease-out-quart' });
  }, []);

  const calculatePrice = (price) => price * (1 - discount / 100);

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setIsModalOpen(true);
    setOrderSubmitted(false);
  };

  const generateOrderId = () => `${Date.now()}-${Math.floor(Math.random() * 1000)}`;

  const handleOrderSubmit = async ({ name, email }) => {
    const newOrderId = generateOrderId();
    setOrderId(newOrderId);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsModalOpen(false);
    setOrderSubmitted(true);
  };

  const plans = [
    {
      name: "Starter",
      desc: "Perfect for solo cleaners or small teams",
      price: 249,
      badge: "Most Popular",
      features: [
        "5 Essential Pages",
        "Mobile-Friendly Design",
        "Service Area Map",
        "Booking Inquiry Form",
        "Social Media Integration",
        "Photo Gallery",
        "Free SSL & Hosting (1st year)",
        "24/7 Support",
      ],
      gradient: "from-primary/10 to-secondary/10",
      color: "bg-primary dark:bg-secondary",
    },
    {
      name: "Growth",
      desc: "Ideal for established cleaning companies",
      price: 599,
      badge: "Best Value",
      features: [
        "Everything in Starter +",
        "Online Booking System",
        "Customer Portal",
        "Payment Processing",
        "Advanced Local SEO",
        "Service Packages",
        "FAQ Section",
        "Priority Support",
      ],
      gradient: "from-primary/10 to-secondary/10",
      color: "bg-gradient-to-r from-primary to-secondary",
    },
  ];

  const pricingSchema = {
    "@context": "https://schema.org",
    "@type": "ProductCollection",
    "name": "Cleaning Business Website Packages",
    "description": "Professional web solutions for cleaning companies",
    "url": "https://dravly.com/pricing",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "itemListElement": plans.map((plan) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": plan.name,
          "description": plan.desc,
          "price": plan.price,
          "priceCurrency": "USD",
          "category": "Website Development"
        }
      }))
    }
  };

  return (
    <>
      <Script id="pricing-schema" type="application/ld+json">
        {JSON.stringify(pricingSchema)}
      </Script>

      <section
        id="pricing"
        className={`
          relative bg-none dark:bg-gradient-to-b dark:from-dark-background dark:to-dark-surface
          overflow-hidden
          ${isStandalonePricingPage
            ? 'pt-40 sm:pt-40 md:pt-60 pb-20'
            : 'pt-20 md:pt-32 pb-16'}
        `}
      >

        {/* Floating Gradient Blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute w-[800px] h-[800px] -top-48 -left-48 bg-gradient-to-r from-primary/30 to-secondary/30 rounded-full blur-3xl opacity-20 animate-blob"></div>
          <div className="absolute w-[600px] h-[600px] -top-32 -right-64 bg-gradient-to-r from-secondary/30 to-primary/30 rounded-full blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute w-[400px] h-[400px] bottom-0 left-1/3 bg-gradient-to-r from-primary/30 to-secondary/30 rounded-full blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        {/* Subtle Grid Pattern */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.02] dark:opacity-[0.05]">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5" />
            </pattern>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {isStandalonePricingPage ? (
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-6">
                Simple Pricing
              </h2>
              <p className="text-lg sm:text-xl text-light-text-secondary dark:text-dark-text-secondary">
                Everything you need to grow your cleaning business online
              </p>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-4">
                Pricing Plans
              </h2>
              <p className="text-light-text-secondary dark:text-dark-text-secondary">
                Choose the perfect plan for your business needs
              </p>
            </div>
          )}

          <CouponComponent
            onCouponApplied={(discount) => setDiscount(discount)}
            onCouponRemoved={() => setDiscount(0)}
            compact={!isStandalonePricingPage}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 mt-8">
            {plans.map((plan, idx) => {
              const discountedPrice = calculatePrice(plan.price);
              return (
                <div
                  key={idx}
                  className={`relative group rounded-3xl p-px overflow-hidden ${plan.color} shadow-2xl`}
                  data-aos="zoom-in"
                  data-aos-delay={idx * 150}
                >
                  <div className="h-full bg-light-surface dark:bg-dark-surface rounded-3xl p-8 flex flex-col">
                    {plan.badge && (
                      <div className={`absolute top-0 right-0 px-5 py-1.5 ${plan.color} text-white rounded-bl-xl rounded-tr-3xl text-sm font-semibold`}>
                        {plan.badge}
                      </div>
                    )}

                    <div className="mb-8">
                      <h3 className="text-3xl font-bold text-light-text-primary dark:text-dark-text-primary mb-2">
                        {plan.name}
                      </h3>
                      <p className="text-light-text-secondary dark:text-dark-text-secondary">
                        {plan.desc}
                      </p>
                    </div>

                    <div className="mb-8">
                      <div className="flex items-end gap-3">
                        {discount > 0 && (
                          <span className="text-xl line-through text-light-text-secondary">
                            ${plan.price}
                          </span>
                        )}
                        <span className="text-5xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                          ${discount > 0 ? discountedPrice.toFixed(2) : plan.price}
                        </span>
                      </div>
                      <p className="text-light-text-secondary dark:text-dark-text-secondary mt-2">
                        One-time payment • No hidden fees
                      </p>
                    </div>

                    <ul className="space-y-4 flex-1 mb-8">
                      {plan.features.map((feature, fIdx) => (
                        <li
                          key={fIdx}
                          className="flex items-center p-3 rounded-xl bg-light-background/50 dark:bg-dark-background/50 hover:bg-light-background dark:hover:bg-dark-background transition-colors"
                        >
                          <svg
                            className="flex-shrink-0 w-6 h-6 text-success mr-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span className="text-light-text-primary dark:text-dark-text-primary">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => handlePlanSelect(plan)}
                      className="w-full py-4 px-8 text-lg font-semibold text-white bg-gradient-to-r from-primary to-secondary rounded-xl transition-all hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary/50 dark:focus:ring-secondary/50"
                    >
                      Get Started
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {isStandalonePricingPage && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20 text-center">
              <div className="p-6" data-aos="fade-up">
                <div className="w-12 h-12 bg-success/10 dark:bg-success/20 rounded-xl flex items-center justify-center mx-auto mb-4">🔒</div>
                <h3 className="text-xl font-semibold mb-2 text-light-text-primary dark:text-dark-text-primary">No Hidden Fees</h3>
                <p className="text-light-text-secondary dark:text-dark-text-secondary">Everything included in one transparent price</p>
              </div>
              <div className="p-6" data-aos="fade-up" data-aos-delay="100">
                <div className="w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-xl flex items-center justify-center mx-auto mb-4">⚡</div>
                <h3 className="text-xl font-semibold mb-2 text-light-text-primary dark:text-dark-text-primary">Lightning Fast</h3>
                <p className="text-light-text-secondary dark:text-dark-text-secondary">Optimized for speed and performance</p>
              </div>
              <div className="p-6" data-aos="fade-up" data-aos-delay="200">
                <div className="w-12 h-12 bg-secondary/10 dark:bg-secondary/20 rounded-xl flex items-center justify-center mx-auto mb-4">🛡️</div>
                <h3 className="text-xl font-semibold mb-2 text-light-text-primary dark:text-dark-text-primary">Secure & Reliable</h3>
                <p className="text-light-text-secondary dark:text-dark-text-secondary">Enterprise-grade security included</p>
              </div>
            </div>
          )}

          {isModalOpen && selectedPlan && (
            <OrderModal
              onClose={() => setIsModalOpen(false)}
              onSubmit={handleOrderSubmit}
              selectedPlan={selectedPlan}
              discount={discount}
              calculatePrice={calculatePrice}
            />
          )}

          {orderSubmitted && orderId && (
            <OrderSuccessModal
              orderId={orderId}
              onClose={() => setOrderSubmitted(false)}
            />
          )}

        
   



        </div>
      </section>
    </>
  );
}
